import type Highcharts from "highcharts";
import type { ManualFailureMetadata } from "../PointMetadata";
import { MANUAL_FAILURE_METADATA } from "../PointMetadata";
import { getFailureColor } from "../../useFailureColor";
import type { Project } from "../../../../api/useProjects";
import type {
  ManualFailureValue,
  IndexedManualFailureValues,
} from "../../../../api/useManualFailureValues";
import type { ActiveAnalysis } from "../../../../api/useActiveAnalysesStream";
import { groupBy } from "diagnostics-lib/iterable";
import { hasDiagnosticData } from "../../useNoDataIntervals";
import type { Quantity } from "../../../../api/useQuantities";
import { MANUAL_FAILURE } from "./serieIds";

const emptyMarker = { symbol: "empty" };
const manualFailureMarker = {
  states: {
    select: {
      radius: 8,
      fillColor: undefined,
      lineColor: undefined,
      lineWidth: 4,
    },
  },
};

const getObservedValue = ({
  failureValue,
  qoi,
}: {
  failureValue: ManualFailureValue;
  qoi: Quantity | null;
}) => {
  if (!qoi || !failureValue) return 0;
  const impacts = failureValue.impacts;
  const impactOnQoi = impacts[qoi.id];
  return impactOnQoi ?? 0;
};

export function getManualFailureSerie({
  indexedManualFailureValues,
  yAxisId,
  manualFailureId,
  manualFailureName,
  analyses,
  qoi,
  legendIndex,
  color,
  actionAllowed,
  type,
  replicateInNavigator,
}: {
  indexedManualFailureValues: IndexedManualFailureValues;
  yAxisId: string;
  manualFailureId: string;
  manualFailureName: string;
  color: string;
  analyses: ActiveAnalysis[];
  qoi: Quantity | null;
  legendIndex: number;
  actionAllowed: boolean;
  type: "line" | "area";
  replicateInNavigator: boolean;
}): Highcharts.SeriesLineOptions | Highcharts.SeriesAreaOptions {
  const data = analyses.reduce<Highcharts.PointOptionsObject[]>((points, analysis, i) => {
    const previousAnalysis = analyses[i - 1];
    const failureValue = indexedManualFailureValues[analysis.id]?.[manualFailureId] ?? undefined;

    if (previousAnalysis !== undefined && previousAnalysis.group !== analysis.group) {
      const custom: ManualFailureMetadata = {
        type: MANUAL_FAILURE_METADATA,
        group: null,
        label: null,
        analysisId: `intercycle-${previousAnalysis.id}-${analysis.id}`,
        failureName: null,
        human: null,
        reparability: null,
        isManual: false,
      };

      points.push({
        id: `intercycle-${previousAnalysis.id}-${analysis.id}`,
        x: (analysis.date + previousAnalysis.date) / 2,
        y: null,
        custom,
      });
    }

    const nextAnalysis = analyses[i + 1];
    const nextFailureValue = nextAnalysis
      ? indexedManualFailureValues[nextAnalysis.id]?.[manualFailureId]
      : undefined;
    const nextImpactOnQoi = qoi === null ? undefined : nextFailureValue?.impacts[qoi.id];

    const previousFailureValue = previousAnalysis
      ? indexedManualFailureValues[previousAnalysis.id]?.[manualFailureId]
      : undefined;
    const previousImpactOnQoi = qoi === null ? undefined : previousFailureValue?.impacts[qoi.id];

    if (failureValue) {
      const observedValue = getObservedValue({ failureValue, qoi });
      const custom: ManualFailureMetadata = {
        type: MANUAL_FAILURE_METADATA,
        group: analysis.group,
        label: analysis.label,
        analysisId: analysis.id,
        failureName: failureValue.manualFailureName,
        human: qoi
          ? {
              value: observedValue,
              unit: qoi.unit,
              precision: 2,
              sign: false,
            }
          : null,
        reparability: failureValue.reparability,
        isManual: true,
      };

      points.push({
        id: `analysis-id-${analysis.id}`,
        x: analysis.date,
        y: observedValue,
        custom,
        marker: {
          symbol: "square",
          lineWidth: 2,
          lineColor: color,
          fillColor: color,
        },
      });
    } else {
      const custom: ManualFailureMetadata = {
        type: MANUAL_FAILURE_METADATA,
        group: analysis.group,
        label: analysis.label,
        analysisId: analysis.id,
        failureName: null,
        human: null,
        reparability: null,
        isManual: false,
      };
      // previous impact has value greater than zero and no next point
      // current analysis has Data
      //  OR next and current are not in same cycle
      const isEndPoint =
        (previousImpactOnQoi !== undefined &&
          previousImpactOnQoi !== 0 &&
          nextImpactOnQoi === undefined &&
          ["DIAGNOSIS", "DIAGNOSIS_ERROR"].includes(analysis.status)) ||
        (previousImpactOnQoi !== undefined &&
          previousImpactOnQoi !== 0 &&
          nextImpactOnQoi === undefined &&
          nextAnalysis?.group !== analysis.group);

      // current analysis has Data but is not an impact and next impact is a point
      // next and current in same cycle
      // OR next impact is a point and next and current are not in same cycle
      const isStartPoint =
        (["DIAGNOSIS", "DIAGNOSIS_ERROR"].includes(analysis.status) &&
          previousImpactOnQoi === undefined &&
          nextImpactOnQoi !== undefined &&
          nextImpactOnQoi !== 0 &&
          nextAnalysis?.group === analysis.group) ||
        (nextImpactOnQoi !== undefined &&
          nextImpactOnQoi !== 0 &&
          previousAnalysis?.group !== analysis.group);

      const isPointBetweentwoManualfailures =
        previousImpactOnQoi !== undefined &&
        nextImpactOnQoi !== undefined &&
        (previousAnalysis?.group === analysis.group || nextAnalysis?.group === analysis.group);

      const hasData = hasDiagnosticData(analysis);

      points.push({
        id: `analysis-id-${analysis.id}`,
        x: analysis.date,
        y: (isStartPoint || isEndPoint || isPointBetweentwoManualfailures) && hasData ? 0 : null,
        marker: emptyMarker,
        custom,
      });
    }

    return points;
  }, []);

  return {
    id: `${MANUAL_FAILURE}-${manualFailureName}`,
    yAxis: yAxisId,
    name: manualFailureName,
    data,
    color,
    type,
    softThreshold: false,
    showInLegend: true,
    legendIndex,
    allowPointSelect: actionAllowed,
    cursor: actionAllowed ? "pointer" : "default",
    replicateInNavigator,
    marker: manualFailureMarker,
  };
}

const getManualFailureSeries = ({
  project,
  yAxisId,
  analyses,
  indexedManualFailureValues,
  qoi,
  startLegendIndex,
  actionAllowed,
  type,
  replicateInNavigator,
}: {
  project: Project;
  yAxisId: string;
  analyses: ActiveAnalysis[];
  indexedManualFailureValues: IndexedManualFailureValues;
  qoi: Quantity | null;
  startLegendIndex: number;
  actionAllowed: boolean;
  type: "line" | "area";
  replicateInNavigator: boolean;
}): (Highcharts.SeriesLineOptions | Highcharts.SeriesAreaOptions)[] => {
  const groubByManualFailure = groupBy(
    Object.values(indexedManualFailureValues).flatMap(m => Object.values(m)),
    "manualFailureId",
  );

  const manualFailures = Array.from(groubByManualFailure.entries())
    .map(([_, manualFailureValue]) => ({
      id: manualFailureValue[0]!.manualFailureId,
      name: manualFailureValue[0]!.manualFailureName,
    }))
    .sort((a, b) => a.name.localeCompare(b.name));

  return manualFailures.map((manualFailure, i) =>
    getManualFailureSerie({
      indexedManualFailureValues,
      yAxisId,
      manualFailureName: manualFailure.name,
      manualFailureId: manualFailure.id,
      color: getFailureColor(project, "MANUAL_FAILURE", manualFailure.name),
      analyses,
      qoi,
      legendIndex: startLegendIndex + i,
      actionAllowed,
      type,
      replicateInNavigator,
    }),
  );
};

export default getManualFailureSeries;
